import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { KPICard } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import OnboardingGuide from '../components/widgets/OnboardingGuide';
import useStore from '../store/useStore';
import { formatCurrency, formatPercent } from '../utils/format';
import { computeSubtotals, computeYtdContributions, formatMonthKeyShort } from '../data/balanceSheetHistory';
import {
  DollarSign, TrendingUp, CreditCard, ArrowLeftRight,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

function MiniChart({ data, dataKey, color = '#10B981', label, formatter, yDomain }) {
  return (
    <ResponsiveContainer width="100%" height={60}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="label" hide />
        {yDomain && <YAxis domain={yDomain} hide />}
        {formatter && (
          <Tooltip
            contentStyle={{ backgroundColor: '#161B22', border: '1px solid #30363D', borderRadius: '8px', padding: '4px 8px' }}
            labelFormatter={(v) => v || ''}
            formatter={(v) => [formatter(v), label || dataKey]}
          />
        )}
        <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#grad-${dataKey})`} strokeWidth={2} dot={false} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

const TAX_ADV_KEYWORDS = ['401k', '401(k)', 'ira', 'hsa', 'roth', 'sep', '403b', '457'];
function isTaxAdvantage(name) {
  const lower = (name || '').toLowerCase();
  return TAX_ADV_KEYWORDS.some((k) => lower.includes(k));
}

export default function Dashboard() {
  const {
    accounts, creditCards, investments, cashFlowMonthly, subscriptions, balanceSheetMonthly, transactions,
    paycheckSnapshots,
  } = useStore(useShallow((s) => ({
    accounts: s.accounts,
    creditCards: s.creditCards,
    investments: s.investments,
    cashFlowMonthly: s.cashFlowMonthly,
    subscriptions: s.subscriptions,
    balanceSheetMonthly: s.balanceSheetMonthly,
    transactions: s.transactions,
    paycheckSnapshots: s.paycheckSnapshots,
  })));

  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  const completedSteps = {
    accounts: accounts.length > 0 || creditCards.length > 0,
    paycheck: paycheckSnapshots.length > 0,
    investments: investments.length > 0,
    subscriptions: subscriptions.length > 0,
  };
  const allStepsComplete = Object.values(completedSteps).every(Boolean);
  const showOnboarding = !allStepsComplete && !onboardingDismissed;

  if (showOnboarding) {
    return <OnboardingGuide completedSteps={completedSteps} onDismiss={() => setOnboardingDismissed(true)} />;
  }

  const now = new Date();

  // ── Proper Total Assets (matches BalanceSheetMonthly) ──
  const cashTotal = accounts
    .filter((a) => ['cash', 'checking', 'savings'].includes(a.type))
    .reduce((s, a) => s + (a.balance || 0), 0);

  const holdingsByAccount = {};
  investments.forEach((inv) => {
    const acct = inv.account || 'Unassigned';
    if (!holdingsByAccount[acct]) holdingsByAccount[acct] = { total: 0 };
    holdingsByAccount[acct].total += inv.currentValue || 0;
  });
  const brokerageTotal = Object.entries(holdingsByAccount)
    .filter(([name]) => !isTaxAdvantage(name))
    .reduce((s, [, d]) => s + d.total, 0);
  const taxAdvTotal = Object.entries(holdingsByAccount)
    .filter(([name]) => isTaxAdvantage(name))
    .reduce((s, [, d]) => s + d.total, 0);
  const holdingNames = Object.keys(holdingsByAccount).map((n) => n.toLowerCase());
  const investAcctTotal = accounts
    .filter((a) => a.type === 'investment' && (a.balance || 0) !== 0)
    .filter((a) => !holdingNames.some((h) => (a.name || '').toLowerCase().includes(h) || h.includes((a.name || '').toLowerCase())))
    .reduce((s, a) => s + (a.balance || 0), 0);
  const investmentsTotal = brokerageTotal + taxAdvTotal + investAcctTotal;

  const propertyTotal = accounts
    .filter((a) => a.type === 'property')
    .reduce((s, a) => s + (a.balance || 0), 0);

  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const otherAssetsTotal = accounts
    .filter((a) => a.type === 'other')
    .reduce((s, a) => s + (a.balance || 0), 0);

  const totalAssets = cashTotal + investmentsTotal + propertyTotal + otherAssetsTotal;

  // ── Proper Total Liabilities (CC + loans) ──
  const ccDebt = creditCards.reduce((sum, c) => sum + Math.abs(c.balance || 0), 0);
  const loansTotal = accounts
    .filter((a) => a.type === 'loan')
    .reduce((s, a) => s + Math.abs(a.balance || 0), 0);
  const totalLiabilities = ccDebt + loansTotal;
  const netWorth = totalAssets - totalLiabilities;

  // MoM change: compare current NW against last month from BS monthly data
  const sortedBS = [...balanceSheetMonthly].filter((r) => r.month).sort((a, b) => a.month.localeCompare(b.month));
  const prevMonthBS = sortedBS.length > 0 ? computeSubtotals(sortedBS[sortedBS.length - 1]) : null;
  const prevNW = prevMonthBS ? prevMonthBS.netWorth : netWorth;
  const nwChange = netWorth - prevNW;
  const nwChangePct = prevNW ? ((nwChange / Math.abs(prevNW)) * 100) : 0;

  const portfolioValue = investments.reduce((sum, i) => sum + i.currentValue, 0);
  const portfolioGain = investments.reduce((sum, i) => sum + i.gainLoss, 0);

  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysElapsed = now.getDate();
  const daysRemaining = daysInMonth - daysElapsed;

  // CC Spend MTD: match CashFlow.jsx formula (endBal - prevMonthBal + ccPayments)
  const prevMo = now.getMonth() === 0 ? 12 : now.getMonth();
  const prevYr = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const prevMonthBSKey = `${prevYr}-${String(prevMo).padStart(2, '0')}`;
  const prevMonthCCBal = (balanceSheetMonthly.find(r => r.month === prevMonthBSKey) || {}).creditCards || 0;
  // Extract YYYY-MM directly from date string to avoid UTC timezone shift
  const currentMonthTxs = (transactions || []).filter(tx => {
    const txMonth = typeof tx.date === 'string' ? tx.date.substring(0, 7) : `${new Date(tx.date).getFullYear()}-${String(new Date(tx.date).getMonth() + 1).padStart(2, '0')}`;
    return txMonth === currentMonthKey;
  });
  const ccPaymentsThisMonth = currentMonthTxs
    .filter(tx => tx.type === 'expense' && tx.category === 'CC Payment')
    .reduce((s, tx) => s + tx.amount, 0);
  const ccSpendMTD = Math.max(0, ccDebt - prevMonthCCBal + ccPaymentsThisMonth);

  // Current month cashflow from transactions
  const cashInflowThisMonth = currentMonthTxs
    .filter(tx => tx.type === 'income')
    .reduce((s, tx) => s + tx.amount, 0);
  const cashOutflowThisMonth = currentMonthTxs
    .filter(tx => tx.type === 'expense')
    .reduce((s, tx) => s + tx.amount, 0);
  const netCashflowThisMonth = cashInflowThisMonth - cashOutflowThisMonth;

  // Subscriptions: normalize frequency (DB may return as string)
  const monthlySubCost = subscriptions.reduce((sum, s) => {
    const freq = typeof s.frequency === 'number' ? s.frequency : (parseInt(s.frequency, 10) || 12);
    return sum + (s.cost * freq) / 12;
  }, 0);

  // Investment YTD % (matches MasterAccounts.jsx formula)
  const decKey = `${now.getFullYear() - 1}-12`;
  const decBS = balanceSheetMonthly.find(r => r.month === decKey) || {};
  const ytdContributions = computeYtdContributions(balanceSheetMonthly, now.getFullYear());
  const totalStarting = (decBS.rothIra || 0) + (decBS.hsa || 0) + (decBS.roth401k || 0) + (decBS.robinhood || 0);
  const totalYtdContrib = (ytdContributions.contribRothIra || 0) + (ytdContributions.contribHsa || 0) + (ytdContributions.contrib401k || 0) + (ytdContributions.contribBrokerage || 0);
  const portfolioYtdGain = portfolioValue - totalStarting - totalYtdContrib;
  const portfolioYtdPct = totalStarting > 0 ? (portfolioYtdGain / totalStarting) * 100 : 0;

  // ── Upcoming Payments — compute next due date from stored day-of-month ──
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const nextDueDate = (dateStr) => {
    const orig = new Date(dateStr + 'T00:00:00');
    const day = orig.getDate();
    let d = new Date(today.getFullYear(), today.getMonth(), day);
    if (d < today) d.setMonth(d.getMonth() + 1);
    return d;
  };

  const upcomingPayments = [
    ...creditCards.filter(c => c.paymentDueDate).map(c => {
      const next = nextDueDate(c.paymentDueDate);
      return {
        name: c.name,
        amount: Math.abs(c.balance),
        date: next,
        type: 'cc',
      };
    }),
  ].sort((a, b) => a.date - b.date).slice(0, 5);

  // ── Net Worth chart from balance_sheet_monthly ──
  const nwChartData = useMemo(() => {
    const points = balanceSheetMonthly
      .filter((r) => r.month)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((r) => {
        const sub = computeSubtotals(r);
        return {
          month: r.month,
          label: formatMonthKeyShort(r.month),
          netWorth: sub.netWorth,
          totalAssets: sub.totalAssets,
          totalLiabilities: sub.totalLiabilities,
          cashTotal: sub.cashTotal,
        };
      });

    // Add or replace current month with live computed values
    const hasCurrentMonth = points.some(p => p.month === currentMonthKey);
    if (!hasCurrentMonth) {
      points.push({
        month: currentMonthKey,
        label: formatMonthKeyShort(currentMonthKey),
        netWorth,
        totalAssets,
        totalLiabilities,
        cashTotal: cashTotal,
      });
    } else {
      const idx = points.findIndex(p => p.month === currentMonthKey);
      if (idx >= 0) {
        points[idx] = {
          ...points[idx],
          netWorth,
          totalAssets,
          totalLiabilities,
          cashTotal: cashTotal,
        };
      }
    }

    return points;
  }, [balanceSheetMonthly, currentMonthKey, netWorth, totalAssets, totalLiabilities, cashTotal]);

  const LinkCard = ({ to, className = '', children }) => (
    <Link to={to} className={`block rounded-xl border border-border bg-bg-card p-5 transition-colors hover:border-accent/40 ${className}`}>
      {children}
    </Link>
  );

  return (
    <div>
      <PageHeader title="Command Center" subtitle={`${now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`} />

      {/* ── Finance ── */}
      <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-4">Finance</h2>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 kpi-grid">
        <LinkCard to="/finance/balance-sheet-monthly">
          <KPICard
            title="Net Worth"
            value={formatCurrency(netWorth)}
            change={nwChangePct}
            changeLabel="MoM"
            icon={DollarSign}
            bare
          />
        </LinkCard>
        <LinkCard to="/finance/cashflow">
          <KPICard
            title="Monthly Cashflow"
            value={formatCurrency(netCashflowThisMonth)}
            change={`${formatCurrency(cashInflowThisMonth)} in / ${formatCurrency(cashOutflowThisMonth)} out`}
            icon={ArrowLeftRight}
            bare
          />
        </LinkCard>
        <LinkCard to="/finance/cashflow">
          <KPICard
            title="CC Spend MTD"
            value={formatCurrency(ccSpendMTD)}
            change={`${daysRemaining} days left`}
            icon={CreditCard}
            bare
          />
        </LinkCard>
        <LinkCard to="/finance/investments">
          <KPICard
            title="Portfolio Value"
            value={formatCurrency(portfolioValue)}
            change={portfolioGain >= 0 ? `+${formatCurrency(portfolioGain)}` : formatCurrency(portfolioGain)}
            icon={TrendingUp}
            bare
          />
        </LinkCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 kpi-grid">
        {/* Net Worth Trend */}
        <LinkCard to="/finance/balance-sheet-monthly" className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-text-secondary">Net Worth Growth</h3>
            <span className="text-xs text-text-muted">
              {nwChartData.length > 0
                ? `${nwChartData[0].label} — ${nwChartData[nwChartData.length - 1].label}`
                : 'No data'}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={nwChartData}>
              <defs>
                <linearGradient id="nwGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#30363D" />
              <XAxis
                dataKey="label"
                tick={{ fill: '#8B949E', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval={Math.max(0, Math.floor(nwChartData.length / 12) - 1)}
              />
              <YAxis
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                tick={{ fill: '#8B949E', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={55}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#161B22', border: '1px solid #30363D', borderRadius: '8px' }}
                labelFormatter={(v) => v}
                formatter={(v) => [formatCurrency(v), 'Net Worth']}
              />
              <Area type="monotone" dataKey="netWorth" stroke="#10B981" fill="url(#nwGradient)" strokeWidth={2} dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </LinkCard>

        {/* Quick Stats */}
        <div className="space-y-4">
          <LinkCard to="/finance/master-accounts">
            <h3 className="text-sm font-medium text-text-secondary mb-3">Credit Card Debt</h3>
            <p className="text-xl font-semibold text-danger">{formatCurrency(ccDebt)}</p>
            <div className="mt-2 space-y-1">
              {creditCards.filter(c => c.balance < 0).map(c => (
                <div key={c.id} className="flex justify-between text-xs">
                  <span className="text-text-secondary">{c.name}</span>
                  <span className="text-text-primary">{formatCurrency(Math.abs(c.balance))}</span>
                </div>
              ))}
            </div>
          </LinkCard>

          <LinkCard to="/finance/subscriptions">
            <h3 className="text-sm font-medium text-text-secondary mb-3">Monthly Subscriptions</h3>
            <p className="text-xl font-semibold text-text-primary">{formatCurrency(monthlySubCost)}/mo</p>
            <p className="text-xs text-text-muted mt-1">{subscriptions.length} active subscriptions</p>
          </LinkCard>

          <LinkCard to="/finance/master-accounts">
            <h3 className="text-sm font-medium text-text-secondary mb-3">Upcoming Payments</h3>
            <div className="space-y-2">
              {upcomingPayments.map((p, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-text-secondary">{p.name}</span>
                  <div className="text-right">
                    <span className="text-text-primary">{formatCurrency(p.amount)}</span>
                    <span className="text-text-muted ml-2">{p.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
              ))}
            </div>
          </LinkCard>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 kpi-grid">
        <LinkCard to="/finance/master-accounts">
          <h3 className="text-sm font-medium text-text-secondary mb-2">Cash & Checking</h3>
          <p className="text-lg font-semibold">{formatCurrency(cashTotal)}</p>
          <MiniChart
            data={nwChartData.slice(-6)}
            dataKey="cashTotal"
            color="#2563EB"
            label="Cash"
            formatter={formatCurrency}
          />
        </LinkCard>
        <LinkCard to="/finance/investments">
          <h3 className="text-sm font-medium text-text-secondary mb-2">Investments</h3>
          <p className="text-lg font-semibold">{formatCurrency(portfolioValue)}</p>
          <p className={`text-xs mt-1 ${portfolioYtdPct >= 0 ? 'text-success' : 'text-danger'}`}>{portfolioYtdPct >= 0 ? '+' : ''}{portfolioYtdPct.toFixed(1)}% YTD</p>
        </LinkCard>
        <LinkCard to="/finance/cashflow">
          <h3 className="text-sm font-medium text-text-secondary mb-2">Daily Spend Rate</h3>
          <p className="text-lg font-semibold">{formatCurrency(daysElapsed > 0 ? ccSpendMTD / daysElapsed : 0)}/day</p>
          <p className="text-xs text-text-muted mt-1">Projected: {formatCurrency(daysElapsed > 0 ? (ccSpendMTD / daysElapsed) * daysInMonth : 0)}/mo</p>
        </LinkCard>
        <LinkCard to="/finance/master-accounts">
          <h3 className="text-sm font-medium text-text-secondary mb-2">Total Points & Miles</h3>
          <p className="text-lg font-semibold">
            {creditCards.reduce((sum, c) => sum + (c.pointsBalance || 0), 0).toLocaleString()}
          </p>
          <p className="text-xs text-text-muted mt-1">Across {creditCards.filter(c => c.pointsBalance > 0).length} programs</p>
        </LinkCard>
      </div>

      {/* ── Health (hidden — coming soon) ── */}
    </div>
  );
}
